import { useState, useEffect, useMemo } from "react";
import { useNavigate, useRouteLoaderData } from "react-router-dom";
import WelcomeBadge from "@/assets/svgs/welcomeBadge.svg";
import {
  Button,
  CircularProgress,
  Typography,
  Box,
  Stack,
  Divider,
  Paper,
} from "@mui/material";
import { WarningOutline } from "@tiny/icons";
import { NotificationDialog } from "@tiny/lib/ConfirmationDialog/DialogTypes/NotificationDialog";
import { useEntri } from "@/components/Entri/useEntri";
import { isFreeDomain } from "../AccountSettings/SenderDetails/utils";
import { SenderDetailsRow } from "../AccountSettings/SenderDetails/SenderDetails.types";

function WelcomeDialog({ redirectPath }: { redirectPath: string }) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);

  const { validateDomain, isValidating } = useEntri();

  const { senderDetails } = useRouteLoaderData("main") as {
    senderDetails: SenderDetailsRow;
  };

  const isFreeEmailDomain = useMemo(
    () => isFreeDomain(senderDetails?.email || ""),
    [senderDetails?.email]
  );

  const missingAuthMethods = useMemo(() => {
    if (isFreeEmailDomain || !senderDetails) return [];

    const missing = [];
    if (!senderDetails.dkimSigned) missing.push("DKIM");
    if (!senderDetails.dmarcSigned) missing.push("DMARC");
    if (!senderDetails.spfSigned) missing.push("SPF");
    return missing;
  }, [senderDetails, isFreeEmailDomain]);

  const shouldShowAuthSetup =
    !isFreeEmailDomain && missingAuthMethods.length > 0 && !setupComplete;

  const loadHandler = () => {
    setIsLoading(false);
  };

  const redirect = () => {
    navigate(redirectPath);
  };

  const handleSetupNow = async () => {
    if (!senderDetails?.email) {
      console.error("No sender email found");
      return;
    }

    await validateDomain({
      email: senderDetails.email,
      onSuccess: () => {
        setSetupComplete(true);
        redirect();
      },
      onError: (error) => {
        console.error("DNS setup failed:", error);
      },
      spfOverrideSupport: true,
    });
  };

  const allSetupItems = [
    {
      label: "DKIM",
      message:
        "Did you know setting up DKIM helps prevent spoofing and ensures your emails are trusted?",
    },
    {
      label: "DMARC",
      message:
        "DMARC adds visibility into who's using your domain and protects against phishing.",
    },
    {
      label: "SPF",
      message:
        "SPF verifies which servers can send on your behalf — improving inbox placement.",
    },
  ];

  const setupItems = shouldShowAuthSetup
    ? allSetupItems.filter((item) => missingAuthMethods.includes(item.label))
    : [];

  return (
    <NotificationDialog
      open
      onClose={redirect}
      content={
        <Box
          sx={{
            textAlign: "center",
            px: { xs: 2, sm: 3 },
            py: 1,
            maxWidth: 500,
            margin: "0 auto",
          }}
        >
          {!isLoading ? (
            <>
              <Typography
                variant="h2"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: "primary.main",
                }}
              >
                Welcome!
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 3,
                }}
              >
                <img
                  alt="welcome badge"
                  src={WelcomeBadge}
                  onLoad={loadHandler}
                  style={{
                    maxWidth: 140,
                    height: "auto",
                    filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.1))",
                  }}
                />
              </Box>

              <Typography
                variant="label1"
                sx={{
                  mb: shouldShowAuthSetup ? 4 : 6,
                  lineHeight: 1.6,
                  color: "text.secondary",
                  fontWeight: 400,
                }}
              >
                {shouldShowAuthSetup
                  ? "Complete setup in 2 minutes to ensure inbox delivery of your emails!"
                  : "We're excited to have you onboard! Your email authentication is all set up. You're ready to start sending emails with confidence."}
              </Typography>

              {shouldShowAuthSetup && !isValidating && (
                <>
                  <Divider
                    sx={{
                      my: 4,
                      borderColor: "divider",
                      opacity: 0.7,
                    }}
                  />

                  <Typography
                    variant="h4R"
                    sx={{
                      mb: 3,
                      fontWeight: 500,
                      color: "text.primary",
                      textAlign: "left",
                    }}
                  >
                    Before you continue...
                  </Typography>

                  <Stack spacing={2.5} sx={{ mb: 4 }}>
                    {setupItems.map(({ label, message }) => (
                      <Paper
                        key={label}
                        variant="outlined"
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          p: 2.5,
                          gap: 2,
                          textAlign: "left",
                          borderRadius: 2,
                          borderColor: "divider",
                          backgroundColor: "background.paper",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                            borderColor: "warning.main",
                            transform: "translateY(-1px)",
                          },
                        }}
                      >
                        <WarningOutline
                          color="warning"
                          sx={{
                            mt: 0.25,
                            fontSize: 20,
                            flexShrink: 0,
                          }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="label2"
                            component="div"
                            sx={{
                              fontWeight: 600,
                              color: "text.primary",
                              mb: 0.5,
                            }}
                          >
                            {label}
                          </Typography>
                          <Typography
                            variant="label3R"
                            component="div"
                            sx={{
                              color: "text.secondary",
                              lineHeight: 1.5,
                            }}
                          >
                            {message}
                          </Typography>
                        </Box>
                      </Paper>
                    ))}
                  </Stack>

                  <Divider
                    sx={{
                      my: 4,
                      borderColor: "divider",
                      opacity: 0.7,
                    }}
                  />
                </>
              )}

              {isValidating && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    py: 4,
                  }}
                >
                  <CircularProgress
                    size={48}
                    sx={{
                      color: "primary.main",
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h4R"
                    sx={{
                      mb: 1,
                      fontWeight: 500,
                      color: "text.primary",
                    }}
                  >
                    Setting up DNS Authentication
                  </Typography>
                  <Typography
                    variant="label2R"
                    sx={{
                      color: "text.secondary",
                      textAlign: "center",
                    }}
                  >
                    Please complete the DNS setup in the popup window...
                  </Typography>
                </Box>
              )}

              {!isValidating && (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={{ xs: 2, sm: 3 }}
                  justifyContent="center"
                  alignItems="center"
                  sx={{ mt: 4 }}
                >
                  {shouldShowAuthSetup ? (
                    <>
                      <Button
                        variant="outlined"
                        onClick={redirect}
                        sx={{
                          minWidth: 140,
                          py: 1.2,
                          borderRadius: 2,
                          order: { xs: 2, sm: 1 },
                        }}
                      >
                        I'll Do It Later
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSetupNow}
                        sx={{
                          minWidth: 140,
                          py: 1.2,
                          borderRadius: 2,
                          boxShadow: 2,
                          order: { xs: 1, sm: 2 },
                          "&:hover": {
                            boxShadow: 4,
                            transform: "translateY(-1px)",
                          },
                        }}
                      >
                        Set Up Now
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={redirect}
                      sx={{
                        minWidth: 160,
                        py: 1.2,
                        borderRadius: 2,
                        boxShadow: 2,
                        "&:hover": {
                          boxShadow: 4,
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      {setupComplete
                        ? "Continue to Dashboard"
                        : "Let's Get Started"}
                    </Button>
                  )}
                </Stack>
              )}
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 6,
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  mb: 4,
                  fontWeight: 600,
                  color: "primary.main",
                }}
              >
                Welcome!
              </Typography>
              <CircularProgress
                size={48}
                sx={{
                  color: "primary.main",
                }}
              />
              <Typography
                variant="label2R"
                sx={{
                  mt: 2,
                  color: "text.secondary",
                }}
              >
                Loading...
              </Typography>
            </Box>
          )}
        </Box>
      }
    />
  );
}

export default WelcomeDialog;
